<template>
  <div class="customer-overview">
    <div
      v-if="customerLoading || documentsLoading"
      id="customerLoading"
      key="customer-loading"
      class="d-flex align-items-center h-100 justify-content-center i-mt-16"
    >
      <i-spinner size="xl"></i-spinner>
    </div>
    <div
      v-else-if="!selectedCustomer"
      id="customer-error"
      key="customer-error"
      data-testid="customer-error"
      class="d-flex align-items-center i-px-5 text-center flex-column h-100 justify-content-center"
    >
      <h4>Something went wrong!</h4>
      <p>
        You might not have permission to view this customer, or it might have
        been deleted.
        <br />
        Try reloading the page.
      </p>
    </div>
    <div v-else key="customer-details" class="ui-wrapper d-flex flex-row">
      <div class="documents-wrapper-container d-flex flex-grow-1">
        <!-- documents list -->
        <div class="documents-wrapper flex-grow-1 position-relative">
          <!-- title with upload -->
          <documents-title-upload
            title="Documents"
            subtitle="View and manage documents for this customer"
            class="documents-title-upload-wrapper"
            :expanded="listIsEmpty && allDocumentsCounts === 0"
          >
            <template #before-dropzone>
              <!-- filters block -->
              <div v-if="allDocumentsCounts > 0" class="filters-wrapper">
                <div class="buttons-container d-flex flex-row i-gap-6">
                  <filter-button
                    v-for="filter in riskFilters"
                    :key="filter.id"
                    :filter="filter"
                    :selected="selectedFilter?.id === filter.id"
                    class="button-filter-risk"
                    @filter-click="onFilterClick"
                    :data-dd-action-name="`customer-view-documents-risk-filter-button-${filter.id}`"
                  />
                </div>
              </div>

              <!-- filter empty state -->
              <div
                v-if="listIsEmpty && allDocumentsCounts > 0"
                class="empty-filter d-flex flex-column align-items-center justify-content-center i-radius-2xl i-gap-8 i-mt-11"
              >
                <i-key-icon
                  :icon="selectedFilter.icon"
                  :type="iKeyIconType.NEUTRAL"
                  :size="iKeyIconSize.XL"
                  :icon-style="iKeyIconStyle.OUTLINE"
                  :shape="iKeyIconShape.SQUARE"
                />
                <div class="text d-flex flex-column align-items-center i-gap-1">
                  <div class="title i-text-button-2">
                    No {{ selectedFilter.label }} documents
                  </div>
                  <div class="subtitle i-text-body-2">
                    Any {{ selectedFilter.label }} documents will appear here
                  </div>
                </div>
              </div>

              <!--documents list empty state -->
              <div
                v-else-if="listIsEmpty"
                data-testid="document-list-empty-state"
                class="empty-state d-flex flex-row justify-content-between align-items-center i-p-11 i-mb-11 i-radius-2xl"
              >
                <div class="text d-flex flex-column i-gap-2">
                  <div class="i-text-neutral-120 i-text-heading-6">
                    Add documents to get started
                  </div>
                  <div class="i-text-neutral-110 i-text-body-2">
                    {{
                      collectEnabled
                        ? 'Upload a document or send a Collect request to your customer'
                        : 'Upload documents to this customer'
                    }}
                  </div>
                </div>
                <i-icon
                  symbol="large-folder-open-1"
                  class="i-text-neutral-80"
                  :size="56"
                ></i-icon>
              </div>
            </template>
          </documents-title-upload>

          <div class="i-mt-11">
            <document-list
              class="customer-documents flex-grow-1"
              :selected-customer="selectedCustomer"
              :selected-filter="selectedFilter"
              @document-deleted="onDocumentDeleted"
            />
          </div>
        </div>

        <!-- details sidebar -->
        <div class="details-sidebar">
          <div class="customer-details">
            <customer-summary :selected-customer="selectedCustomer" />
          </div>

          <div v-if="hasParsedDetails" class="customer-details">
            <customer-parsed-details-list
              :customer="selectedCustomer"
              :show-blocklist-suggestions="showBlocklistSuggestions"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {
  iIcon,
  iSpinner,
  iKeyIcon,
  iKeyIconShape,
  iKeyIconType,
  iKeyIconStyle,
  iKeyIconSize,
} from '@inscribe/signal';
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex';
import DocumentList from '@/V2/documents/document-list/DocumentList';
import CustomerSummary from '@/V2/customers/CustomerSummary';
import CustomerParsedDetailsList from '@/V2/customers/CustomerParsedDetailsList';
import FilterButton from '@/V2/components/filters/FilterButton';
import { updateQueryParams } from '@/utils/queryParams';
import DocumentsTitleUpload from '@/V2/documents/DocumentsTitleUpload';

export default {
  components: {
    iIcon,
    iSpinner,
    iKeyIcon,
    CustomerParsedDetailsList,
    CustomerSummary,
    DocumentList,
    FilterButton,
    DocumentsTitleUpload,
  },
  beforeRouteLeave(to, from, next) {
    const isUploading = this.someDocumentUploading();

    if (
      isUploading &&
      !window.confirm(
        'Some documents are still uploading. Are you sure you want to leave?',
      )
    ) {
      next(false);
    } else {
      next();
    }
  },
  props: {
    isPdfExportEnabled: {
      type: Boolean,
      default: false,
    },
    isCompletePdfExport: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      iKeyIconShape,
      iKeyIconType,
      iKeyIconStyle,
      iKeyIconSize,
    };
  },
  watch: {
    '$route.fullPath': {
      handler: function () {
        this.refreshDocumentList();
      },
    },
  },
  computed: {
    ...mapGetters([
      'documentsListEmpty',
      'someDocumentUploading',
      'getCachedCustomerById',
      'collectEnabled',
    ]),
    ...mapState({
      currentUser: (state) => state.currentUser,
      documentsLoading: (state) => state.documentsLoading,
      selectedCustomer: (state) => state.customers.selectedCustomer,
      customerLoading: (state) => state.customers.customerLoading,
      showBlocklistSuggestions: (state) =>
        state.customers.showBlocklistSuggestions,
    }),
    allDocumentsCounts() {
      return (
        this.getCachedCustomerById(this.selectedCustomer.id)?.no_of_documents ||
        0
      );
    },
    selectedFilter() {
      const riskLevels = this.$route.query.risk_levels;
      return (
        this.riskFilters?.find(
          (filter) =>
            filter.queryParam === riskLevels ||
            (Array.isArray(riskLevels) &&
              riskLevels.includes(filter.queryParam)),
        ) || this.riskFilters[0]
      );
    },
    listIsEmpty() {
      return (
        this.$store.state.documents.length === 0 &&
        !this.$store.state.documentsLoading
      );
    },
    riskFilters() {
      const summary = this.selectedCustomer?.document_risk_counts || {};
      return [
        {
          id: 'all',
          title: 'All Documents',
          subtitle: `${this.allDocumentsCounts === 1 ? '1 document' : `${this.allDocumentsCounts} documents`}`,
          icon: 'small_solid_folder',
          queryParam: '',
          label: 'high risk',
        },
        {
          id: 'low',
          title: 'Low Risk',
          subtitle: `${summary.low === 1 ? '1 document' : `${summary.low} documents`}`,
          icon: 'small_solid_check',
          queryParam: 'LOW',
          label: 'low risk',
        },
        {
          id: 'medium',
          title: 'Medium Risk',
          subtitle: `${summary.medium === 1 ? '1 document' : `${summary.medium} documents`}`,
          icon: 'small_solid_alert_circle',
          queryParam: 'MEDIUM',
          label: 'medium risk',
        },
        {
          id: 'high',
          title: 'High Risk',
          subtitle: `${summary.high === 1 ? '1 document' : `${summary.high} documents`}`,
          icon: 'small_solid_alert_triangle',
          queryParam: 'HIGH',
          label: 'high risk',
        },
      ];
    },
    routeKey() {
      return this.$route.params.customer_id + this.$route.params.document_id;
    },
    hasParsedDetails() {
      return (
        !this.documentsLoading &&
        this.selectedCustomer?.parsedDetails?.length > 0
      );
    },
  },
  mounted() {
    this.refreshDocumentList();
    document.body.classList.add('redesign-customer-view-body');
  },
  unmounted() {
    document.body.classList.remove('redesign-customer-view-body');
  },
  methods: {
    ...mapActions(['getPaginatedDocuments']),
    ...mapMutations(['setDocumentsLoading']),

    onDocumentDeleted(docData) {
      // Refresh the document list after deletion
      this.refreshDocumentList();
    },
    onFilterClick(filter) {
      updateQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
        updates: {
          risk_levels: [filter.queryParam],
        },
      });
    },
    refreshDocumentList() {
      if (this.selectedCustomer?.id) {
        this.setDocumentsLoading(true);
        this.getPaginatedDocuments({
          customerId: this.selectedCustomer.id,
          page: this.$route.query.page || 1,
          risk_levels: this.$route.query.risk_levels || [],
        })
          .then(({ data }) => {
            this.$store.commit('setDocuments', data.data);
            if (data.metadata) {
              const pagination = data.metadata.pagination;
              const fraudResults = data.metadata.fraud_results;
              this.$store.commit('setDocumentsMetadata', {
                pagination,
                fraudResults,
              });
            }
            this.setDocumentsLoading(false);
          })
          .catch(() => {
            // Handle error silently or show toast if needed
          });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$header-bar-height: 73px;
$header-bar-height-with-tabs: 120px;
$pagination-bar-height: 60px;

.customer-overview {
  .flex-column {
    display: flex;
    flex-direction: column;
  }

  .empty-filter {
    width: 100%;
    max-width: 1200px;
    margin-inline: auto;
    border: 1px solid var(--border-neutral-subtle);
    padding: 72px 32px;
    background: var(--base);

    .title {
      color: var(--text-neutral-bold);
    }

    .subtitle {
      color: var(--text-neutral);
    }
  }

  .empty-state {
    border: 1px solid $light-neutral-60;
  }

  .ui-wrapper {
    width: 100%;

    .documents-wrapper {
      overflow-y: auto;
      position: relative;
      height: calc(100vh - #{$header-bar-height});
      padding: 32px;
      padding-top: 0px;
      background: $light-neutral-default;

      &.pagination {
        height: calc(100vh - #{$header-bar-height} - #{$pagination-bar-height});
      }

      .filters-wrapper {
        width: 100%;

        .buttons-container {
          margin: 0 auto;

          .button-filter-risk {
            width: 25%;
          }
        }
      }

      .documents-title-upload-wrapper {
        max-width: 1200px;
        margin: 0 auto;
        margin-top: 32px;
      }

      .customer-documents {
        max-width: 1200px;
        margin: 0 auto;

        .empty-state {
          img {
            max-width: 150px;
          }
        }
      }
    }

    .details-sidebar {
      flex-shrink: 0;
      background-color: $color-white;
      border-left: 1px solid $color-navy-blue-20;
      overflow: hidden;
      position: relative;
      height: calc(100vh - #{$header-bar-height});

      .customer-details {
        @extend .flex-column;

        font-size: $font-size-secondary;
        max-width: 225px;
        width: calc(100vw - #{$padding-container * 2});
        overflow: auto;

        &:first-child {
          border-bottom: 1px solid $color-navy-blue-20;
        }
      }
    }
  }
}
</style>
